import { useEffect, useRef, MutableRefObject } from "react";
import { clearServiceWorkerCache } from "@/lib/clearServiceWorkerCache";
import { fetchAccount } from "@/modules/account/service";
import { Account } from "@/types/account";
import { QueryClient } from "@tanstack/react-query";

interface UseCrossTabSyncParams {
  setImpersonateCognitoId: (value: string | null) => void;
  setLoadingImpersonation: (loading: boolean) => void;
  setImpersonationMessage: (message: string) => void;
  setAccount: (account: Account | null) => void;
  setImpersonationReturnPath: (path: string | null) => void;
  impersonationReturnPath: string | null;
  locationPathname: string;
  queryClientRef: MutableRefObject<QueryClient | undefined>;
  navigateRef: MutableRefObject<(path: string) => void>;
  tRef: MutableRefObject<(key: string) => string>;
  toastRef: MutableRefObject<
    (options: {
      title: string;
      description?: string;
      variant?: "destructive";
    }) => void
  >;
}

export const useCrossTabSync = ({
  setImpersonateCognitoId,
  setLoadingImpersonation,
  setImpersonationMessage,
  setAccount,
  setImpersonationReturnPath,
  impersonationReturnPath,
  locationPathname,
  queryClientRef,
  navigateRef,
  tRef,
  toastRef,
}: UseCrossTabSyncParams) => {
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);

  useEffect(() => {
    if (!broadcastChannelRef.current) {
      broadcastChannelRef.current = new BroadcastChannel("impersonation");
    }

    const channel = broadcastChannelRef.current;

    const handleMessage = async (event: MessageEvent) => {
      if (event.data.type === "impersonation-change") {
        setLoadingImpersonation(true);
        const loadingKey = event.data.impersonateCognitoId
          ? "impersonation.loading.start"
          : "impersonation.loading.stop";
        setImpersonationMessage(tRef.current(loadingKey));

        try {
          setImpersonateCognitoId(event.data.impersonateCognitoId);

          await clearServiceWorkerCache();
          queryClientRef.current?.clear();

          const signedInAccount = await fetchAccount();
          if (signedInAccount !== undefined && signedInAccount !== null) {
            if (
              event.data.impersonateCognitoId &&
              !signedInAccount.authenticated_as
            ) {
              toastRef.current({
                title: tRef.current("impersonation.error.title"),
                description: tRef.current("impersonation.error.missingData"),
                variant: "destructive",
              });
              setImpersonateCognitoId(null);
              setImpersonationReturnPath(null);
              await clearServiceWorkerCache();
              queryClientRef.current?.clear();
              const adminAccount = await fetchAccount();
              if (adminAccount !== undefined) {
                setAccount(adminAccount);
              }
              return;
            }
            setAccount(signedInAccount);
          }

          if (event.data.impersonateCognitoId) {
            setImpersonationReturnPath(locationPathname);
            navigateRef.current("/");
          } else {
            const returnPath = impersonationReturnPath || "/";
            navigateRef.current(returnPath);
            setImpersonationReturnPath(null);
          }
        } finally {
          setLoadingImpersonation(false);
          setImpersonationMessage("");
        }
      }
    };

    channel.addEventListener("message", handleMessage);

    return () => {
      channel.removeEventListener("message", handleMessage);
    };
  }, [
    setImpersonateCognitoId,
    queryClientRef,
    navigateRef,
    tRef,
    toastRef,
    locationPathname,
    impersonationReturnPath,
    setLoadingImpersonation,
    setImpersonationMessage,
    setAccount,
    setImpersonationReturnPath,
  ]);

  return broadcastChannelRef;
};
